"use client"

import React, { useEffect, useRef, useState } from "react"
import {
  Wand2,
  ChevronDown,
  ChevronRight,
  FileText,
  CheckCircle2,
  LayoutGrid,
  Network,
  PenTool,
  Globe,
  X,
  Check,
  Loader2,
  BrainCircuit,
  ListChecks,

  Search,
  Truck,
  Users,
  Send,
} from "lucide-react"
import { Z } from "@/lib/z-index"
import { GlassSidePanel } from "@/src/components/ui/glass-side-panel"

import type { SubAgent, AgentDetailStep } from "@/lib/agent-demo-data"
import type { RichCard } from "@/lib/agent-demo-data"
import { ToolCallItem } from "./tool-call-item"
import { TypingText } from "./typing-text"
import { RichCardsCompact, RichCardRenderer } from "@/src/components/demo/rich-card-renderer"
import "./agent-sidebar.less"

const ROLE_ICONS: Record<string, React.ReactNode> = {
  "环境": <LayoutGrid className="h-4 w-4 text-primary" />,
  "数据": <FileText className="h-4 w-4 text-primary" />,
  "研究": <Search className="h-4 w-4 text-primary" />,
  "后勤": <Truck className="h-4 w-4 text-primary" />,
  "协作": <Users className="h-4 w-4 text-primary" />,
  "整合": <Wand2 className="h-4 w-4 text-primary" />,
  "设计": <PenTool className="h-4 w-4 text-primary" />,
  "交付": <Send className="h-4 w-4 text-primary" />,
}

/* ── Status badge ── */
function StatusBadge({ status }: { status: SubAgent["status"] }) {
  if (status === "done")
    return (
      <span className="flex items-center gap-1 text-caption font-medium text-primary">
        <Check className="h-3 w-3" /> 已完成
      </span>
    )
  if (status === "running")
    return (
      <span className="flex items-center gap-1 text-caption font-medium text-primary">
        <Loader2 className="h-3 w-3 animate-spin" /> 执行中
      </span>
    )
  return <span className="text-caption text-muted-foreground">等待中</span>
}

/* ── Collapsible Thinking — streams when isStreaming=true ── */
function ThinkingBlock({ content, isDone, isStreaming, onStreamDone }: { content: string; isDone: boolean; isStreaming: boolean; onStreamDone?: () => void }) {
  const [fullyExpanded, setFullyExpanded] = useState(true)
  const [streamFinished, setStreamFinished] = useState(!isStreaming)

  return (
    <div className="animate-in fade-in duration-300">
      <button
        onClick={() => setFullyExpanded(!fullyExpanded)}
        className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors group w-full"
      >
        <BrainCircuit className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="text-xs font-medium">思考中</span>
        {fullyExpanded
          ? <ChevronDown className="h-3 w-3 opacity-50 group-hover:opacity-100 transition-opacity" />
          : <ChevronRight className="h-3 w-3 opacity-50 group-hover:opacity-100 transition-opacity" />}
      </button>
      <div
        className="overflow-hidden transition-all duration-200 ease-out"
        style={{
          maxHeight: fullyExpanded ? "1000px" : "0px",
          opacity: fullyExpanded ? 1 : 0,
        }}
      >
        <p className="text-xs text-muted-foreground/80 mt-1 ml-5 leading-relaxed">
          {isStreaming
            ? <TypingText text={content} speed={14} onComplete={() => { setStreamFinished(true); onStreamDone?.() }} />
            : content}
        </p>
      </div>
    </div>
  )
}

/* ── Planning block with step-by-step completion ── */
function PlanningBlock({
  content,
  isStreaming,
  onStreamDone,
  completedCount = 0,
}: {
  content: string
  isStreaming: boolean
  onStreamDone?: () => void
  completedCount?: number
}) {
  const lines = content.split("\n").filter(Boolean)
  const [expanded, setExpanded] = useState(true)
  const [visibleLines, setVisibleLines] = useState(isStreaming ? 1 : lines.length)

  const handleLineDone = () => {
    if (visibleLines < lines.length) {
      setVisibleLines((v) => v + 1)
    } else {
      onStreamDone?.()
    }
  }

  const stripNumber = (line: string) => line.replace(/^\d+\.\s*/, "")

  return (
    <div className="animate-in fade-in duration-300">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 agent-plan-trigger hover:opacity-80 transition-opacity group"
      >
        <ListChecks className="h-3.5 w-3.5" />
        <span className="text-xs font-medium">执行计划</span>
        {completedCount >= lines.length && completedCount > 0 && (
          <span className="text-micro text-primary font-medium ml-1">全部完成</span>
        )}
        {expanded
          ? <ChevronDown className="h-3 w-3 opacity-50 group-hover:opacity-100" />
          : <ChevronRight className="h-3 w-3 opacity-50 group-hover:opacity-100" />}
      </button>
      {expanded && (
        <div className="mt-2 ml-1 flex flex-col gap-1 animate-in fade-in duration-200">
          {lines.slice(0, isStreaming ? visibleLines : lines.length).map((line, i) => {
            const isLast = i === visibleLines - 1
            const shouldStream = isStreaming && isLast
            const isDone = i < completedCount
            const isActive = !isDone && i === completedCount && completedCount < lines.length && !isStreaming

            return (
              <div
                key={i}
                className={`flex items-center gap-2 py-1 px-2 rounded-md transition-all duration-300 ${
                  isDone
                    ? "bg-primary/5"
                    : isActive
                      ? "bg-secondary/40"
                      : ""
                }`}
              >
                {/* Status indicator */}
                <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
                  {isDone ? (
                    <CheckCircle2 className="h-3.5 w-3.5 text-primary animate-in zoom-in-50 duration-200" />
                  ) : isActive ? (
                    <Loader2 className="h-3.5 w-3.5 text-primary animate-spin" />
                  ) : (
                    <div className="h-2 w-2 rounded-full border border-muted-foreground/30" />
                  )}
                </div>

                {/* Text */}
                <span
                  className={`text-xs leading-relaxed transition-all duration-300 ${
                    isDone
                      ? "line-through text-muted-foreground/50"
                      : isActive
                        ? "text-foreground font-medium"
                        : "text-muted-foreground"
                  }`}
                >
                  {shouldStream
                    ? <TypingText text={stripNumber(line)} speed={12} onComplete={handleLineDone} />
                    : stripNumber(line)}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/* ── Lightweight inline markdown ── */
function MiniMarkdown({ text }: { text: string }) {
  const lines = text.split("\n")
  return (
    <>
      {lines.map((line, li) => {
        if (!line.trim()) return <br key={li} />
        const isQuote = line.startsWith("> ")
        const content = isQuote ? line.slice(2) : line
        // Parse **bold** spans
        const parts = content.split(/(\*\*[^*]+\*\*)/)
        const rendered = parts.map((p, pi) => {
          if (p.startsWith("**") && p.endsWith("**")) {
            return <strong key={pi} className="font-semibold text-foreground">{p.slice(2, -2)}</strong>
          }
          return <span key={pi}>{p}</span>
        })
        if (isQuote) {
          return (
            <span key={li} className="block mt-1.5 pl-2.5 border-l-2 border-primary/30 text-foreground/90 font-medium">
              {rendered}
            </span>
          )
        }
        return <span key={li} className="block">{rendered}</span>
      })}
    </>
  )
}

/* ── Result block ── */
function ResultBlock({ content, isStreaming, onStreamDone }: { content: string; isStreaming: boolean; onStreamDone?: () => void }) {
  return (
    <div className="animate-in fade-in duration-300 rounded-lg border px-3 py-2.5 agent-result-surface">
      <div className="text-xs text-foreground/85 leading-relaxed">
        {isStreaming ? <TypingText text={content} speed={13} onComplete={onStreamDone} /> : <MiniMarkdown text={content} />}
      </div>
    </div>
  )
}

/* ── Tool reference ── */
function ToolRef({ agent, step, onDisplayed }: { agent: SubAgent; step: AgentDetailStep; onDisplayed?: () => void }) {
  const skill = agent.skills[step.skillRef!]
  const tc = skill?.toolCalls[step.toolRef!]
  useEffect(() => {
    if (skill && tc && onDisplayed) onDisplayed()
  }, [skill, tc, onDisplayed])

  if (!skill || !tc) return null
  const displayTc = tc.status === "pending" ? { ...tc, status: "running" as const } : tc
  return (
    <div className="animate-in fade-in duration-300">
      <ToolCallItem toolCall={displayTc} />
    </div>
  )
}

/* ═══════════════════════════════════════════════════════ */
export function AgentSidebar({
  agent,
  // startTimes: when each agent became "running" (ms since epoch)
  startTimes,
  onClose,
  richCards,
}: {
  agent: SubAgent | null
  startTimes: Record<string, number>
  onClose: () => void
  richCards?: RichCard[]
}) {
  const scrollRef = useRef<HTMLDivElement>(null)

  // Compute visible step count based on elapsed time, only re-render when it changes
  const [visibleCount, setVisibleCount] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    const compute = () => {
      if (!agent) return 0
      if (agent.status === "done") return agent.detailSteps.length
      const startedAt = startTimes[agent.id]
      if (startedAt === undefined) return 0
      const elapsed = Date.now() - startedAt
      let count = 0
      for (const step of agent.detailSteps) {
        if (step.delay <= elapsed) count++
        else break
      }
      return count
    }
    const tick = () => {
      const next = compute()
      setVisibleCount((prev) => (prev === next ? prev : next))
    }
    tick()
    intervalRef.current = setInterval(tick, 300)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [agent, startTimes])

  const stepsToShow = agent ? agent.detailSteps.slice(0, visibleCount) : []
  const isDone = agent?.status === "done"

  // Sequential streaming: track how many text-based steps have finished streaming.
  // Only the step at index === streamedUpTo is actively streaming.
  // Steps before it render instantly; steps after wait until it completes.
  const [streamedUpTo, setStreamedUpTo] = useState(0)
  const prevAgentIdRef = useRef<string | null>(null)

  // Reset on agent change — 不因 isDone 跳过流式，保持按序输出
  if (agent && agent.id !== prevAgentIdRef.current) {
    prevAgentIdRef.current = agent.id
    // If agent is already done, skip all streaming — show everything instantly
    if (agent.status === "done") {
      setStreamedUpTo(agent.detailSteps.length)
    } else {
      setStreamedUpTo(visibleCount > 0 ? visibleCount - 1 : 0)
    }
  }

  const advanceStream = () => {
    setStreamedUpTo((prev) => prev + 1)
  }



  // Auto-scroll: keep bottom in view during streaming and when new steps appear.
  // MutationObserver detects DOM changes from TypingText character rendering,
  // using synchronous scrollTop (not smooth) to avoid jitter from chasing a moving target.
  const userScrolledUpRef = useRef(false)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = scrollRef.current
    if (!container) return
    const handleScroll = () => {
      const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 40
      userScrolledUpRef.current = !atBottom
    }
    container.addEventListener("scroll", handleScroll, { passive: true })
    return () => container.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const container = scrollRef.current
    const content = contentRef.current
    if (!container || !content) return
    let rafId = 0
    const scrollToBottom = () => {
      if (!userScrolledUpRef.current) {
        container.scrollTop = container.scrollHeight
      }
    }
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(scrollToBottom)
    })
    mo.observe(content, { childList: true, subtree: true, characterData: true })
    return () => { mo.disconnect(); cancelAnimationFrame(rafId) }
  }, [agent?.id])

  // === Enter / Exit animation ===
  type SidebarPhase = "closed" | "entering" | "open" | "exiting"
  const [sidebarPhase, setSidebarPhase] = useState<SidebarPhase>("closed")
  const displayAgentRef = useRef<SubAgent | null>(null)
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Only react to presence change (null ↔ non-null), not every agent object update
  const hasAgent = agent !== null
  useEffect(() => {
    if (hasAgent) {
      // Opening — only animate if not already open/entering
      if (exitTimerRef.current) { clearTimeout(exitTimerRef.current); exitTimerRef.current = null }
      setSidebarPhase((prev) => {
        if (prev === "open" || prev === "entering") return prev
        return "entering"
      })
      const enterTimer = setTimeout(() => setSidebarPhase("open"), 50)
      return () => clearTimeout(enterTimer)
    } else if (displayAgentRef.current) {
      // Closing — keep mounted for exit animation
      setSidebarPhase("exiting")
      exitTimerRef.current = setTimeout(() => {
        setSidebarPhase("closed")
        displayAgentRef.current = null
      }, 400)
    }
    return () => { if (exitTimerRef.current) clearTimeout(exitTimerRef.current) }
  }, [hasAgent])

  // Keep displayAgent ref up-to-date on every render (without triggering animation)
  if (agent) displayAgentRef.current = agent
  const displayAgent = displayAgentRef.current

  if (sidebarPhase === "closed" || !displayAgent) return null

  const isOpen = sidebarPhase === "open"

  return (
      <GlassSidePanel
        side="right"
        open={isOpen}
        onOpenChange={(v) => { if (!v) onClose() }}
        layout="full"
        topOffset="4.5rem"
        margin={1}
        maxWidth="max-w-md"
        showCapsule={false}
        showOverlay={true}
        overlayClassName="lg:hidden"
        panelStyleClass="agent-sidebar-panel"
        className="group/sidebar chat-teal-theme"
        zIndex={Z.NAV_PILL}
      >
        {/* Close button — absolute top-left, matching left panel's capsule inset */}
        <button
          onClick={onClose}
          className="glass-child absolute flex items-center justify-center rounded-full text-foreground/60 hover:text-foreground hover:scale-105 active:scale-95 transition-all"
          style={{ top: 8, left: 8, width: 34, height: 34, zIndex: 1 }}
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-2.5 border-b border-border/30 pr-4 flex-shrink-0" style={{ minHeight: 50, paddingTop: 8, paddingBottom: 8, paddingLeft: 50 }}>
          <h3 className="text-base font-semibold text-foreground truncate">{displayAgent.name}</h3>
          <span
            className="glass-child-badge rounded-full px-2 py-0.5 text-micro font-medium text-foreground/60 shrink-0"
          >
            {displayAgent.role}
          </span>
          <div className="ml-auto shrink-0">
            <StatusBadge status={displayAgent.status} />
          </div>
        </div>

        {/* Stream */}
        <div ref={scrollRef} className="flex-1 overflow-y-scroll overflow-x-hidden px-4 py-4 glass-scroll" style={{ overscrollBehavior: "contain" }}>
          <div ref={contentRef}>
          {stepsToShow.length === 0 ? (
            <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">准备中...</span>
            </div>
          ) : (
            <div className="flex flex-col gap-3.5">
              {stepsToShow.map((step, i) => {
                const isTextStep = step.type === "thinking" || step.type === "planning" || step.type === "text" || step.type === "result"
                const isStreaming = isTextStep && i === streamedUpTo
                if (i > streamedUpTo) return null

                let stepNode: React.ReactNode = null

                if (step.type === "thinking")
                  stepNode = <ThinkingBlock key={`${displayAgent.id}-t-${i}`} content={step.content!} isDone={isDone} isStreaming={isStreaming} onStreamDone={advanceStream} />
                else if (step.type === "planning") {
                  const planLines = step.content!.split("\n").filter(Boolean).length
                  let completedCount = 0
                  if (isDone) {
                    completedCount = planLines
                  } else {
                    const stepsAfterPlan = stepsToShow.slice(i + 1).filter(s => s.type !== "planning")
                    const totalAfterPlan = displayAgent.detailSteps.slice(i + 1).filter(s => s.type !== "planning").length
                    if (totalAfterPlan > 0 && planLines > 0) {
                      completedCount = Math.min(
                        planLines,
                        Math.floor((stepsAfterPlan.length / totalAfterPlan) * planLines)
                      )
                    }
                  }
                  stepNode = <PlanningBlock key={`${displayAgent.id}-p-${i}`} content={step.content!} isStreaming={isStreaming} onStreamDone={advanceStream} completedCount={completedCount} />
                }
                else if (step.type === "text")
                  stepNode = (
                    <p key={`${displayAgent.id}-tx-${i}`} className="animate-in fade-in duration-300 text-sm text-foreground/85 leading-relaxed">
                      {isStreaming ? <TypingText text={step.content!} speed={16} onComplete={advanceStream} /> : step.content}
                    </p>
                  )
                else if (step.type === "result")
                  stepNode = <ResultBlock key={`${displayAgent.id}-r-${i}`} content={step.content!} isStreaming={isStreaming} onStreamDone={advanceStream} />
                else if (step.type === "tool" && step.skillRef !== undefined && step.toolRef !== undefined)
                  stepNode = (
                    <ToolRef
                      key={`${displayAgent.id}-tool-${i}`}
                      agent={displayAgent}
                      step={step}
                      onDisplayed={i === streamedUpTo ? advanceStream : undefined}
                    />
                  )

                return (
                  <React.Fragment key={`${displayAgent.id}-frag-${i}`}>
                    {stepNode}
                    {step.richCards && i < streamedUpTo && step.richCards.map((card, ci) => (
                      <div key={`${displayAgent.id}-rc-${i}-${ci}`} className="animate-in fade-in slide-in-from-bottom-1 duration-300">
                        <RichCardRenderer card={card} />
                      </div>
                    ))}
                  </React.Fragment>
                )
              })}

              {/* Still running indicator */}
              {displayAgent.status === "running" && visibleCount >= displayAgent.detailSteps.length && (
                <div className="flex items-center gap-2 text-muted-foreground py-1">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span className="text-xs">处理中...</span>
                </div>
              )}

              {/* Rich cards compact preview — when agent is done */}
              {displayAgent.status === "done" && richCards && richCards.length > 0 && (
                <RichCardsCompact cards={richCards} />
              )}
            </div>
          )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border/30 px-4 py-2.5 flex-shrink-0">
          <div className="flex items-center justify-between text-caption text-muted-foreground">
            <span>
              {displayAgent.skills.reduce((acc, s) => acc + s.toolCalls.filter((t) => t.status === "done").length, 0)}
              {" / "}
              {displayAgent.skills.reduce((acc, s) => acc + s.toolCalls.length, 0)}
              {" 步完成"}
            </span>
            <span className="font-mono text-micro text-muted-foreground/40">{displayAgent.id}</span>
          </div>
        </div>
      </GlassSidePanel>
  )
}
